// Sessão 22/06: Tela de revisão da Fila de Divergências do Conector iSolve
// Rota: /divergencias
// Ref: SPEC_CONECTOR_INCREMENTAL v1.1 §6

import { useState, useEffect, useCallback } from 'react';
import { divergenciasApi } from '../services/api';
import type { DivergenciaConector, TipoDivergencia, StatusDivergencia } from '../types';
import { TIPO_DIVERGENCIA_LABEL } from '../types';

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatarData(iso?: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function badgeTipo(tipo: TipoDivergencia) {
  // CRITICA_CCV/DOIS_TEMPOS_* - tipos mais novos que a fila geral ainda não distinguia
  // visualmente (ver types/index.ts); cores adicionadas aqui só para o Record continuar
  // exaustivo, sem mudar o comportamento existente dos 3 tipos originais.
  const cores: Record<TipoDivergencia, string> = {
    INATIVACAO_COMPRADOR:       'bg-red-100 text-red-800',
    DIVERGENCIA_CAMPO:          'bg-yellow-100 text-yellow-800',
    REGISTRO_NOVO:              'bg-blue-100 text-blue-800',
    CRITICA_CCV:                'bg-purple-100 text-purple-800',
    DOIS_TEMPOS_ASSINATURA:     'bg-indigo-100 text-indigo-800',
    DOIS_TEMPOS_ALTO_PADRAO:    'bg-indigo-100 text-indigo-800',
    DOIS_TEMPOS_VEDACAO_LIMITE: 'bg-indigo-100 text-indigo-800',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${cores[tipo]}`}>
      {TIPO_DIVERGENCIA_LABEL[tipo]}
    </span>
  );
}

function badgeStatus(status: StatusDivergencia) {
  const cores: Record<StatusDivergencia, string> = {
    PENDENTE:  'bg-orange-100 text-orange-800',
    APROVADO:  'bg-green-100 text-green-800',
    REJEITADO: 'bg-gray-100 text-gray-600',
  };
  const labels: Record<StatusDivergencia, string> = {
    PENDENTE:  'Pendente',
    APROVADO:  'Aprovado',
    REJEITADO: 'Rejeitado',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${cores[status]}`}>
      {labels[status]}
    </span>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────

export default function Divergencias() {
  const [items, setItems]               = useState<DivergenciaConector[]>([]);
  const [carregando, setCarregando]     = useState(true);
  const [erro, setErro]                 = useState<string | null>(null);

  // Filtros
  const [filtroTipo, setFiltroTipo]     = useState('');
  const [filtroStatus, setFiltroStatus] = useState('PENDENTE');
  const [filtroDE, setFiltroDE]         = useState('');
  const [filtroATE, setFiltroATE]       = useState('');

  // Resolução em andamento (id → 'aprovando'|'rejeitando')
  const [resolvendo, setResolvendo]     = useState<Record<string, string>>({});

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const data = await divergenciasApi.listar({
        tipo:   filtroTipo   || undefined,
        status: filtroStatus || undefined,
        de:     filtroDE     || undefined,
        ate:    filtroATE    || undefined,
      });
      setItems(data);
    } catch {
      setErro('Erro ao carregar divergências. Verifique a conexão.');
    } finally {
      setCarregando(false);
    }
  }, [filtroTipo, filtroStatus, filtroDE, filtroATE]);

  useEffect(() => { carregar(); }, [carregar]);

  async function resolver(id: string, acao: 'APROVAR' | 'REJEITAR') {
    setResolvendo(r => ({ ...r, [id]: acao === 'APROVAR' ? 'aprovando' : 'rejeitando' }));
    try {
      const atualizada = acao === 'APROVAR'
        ? await divergenciasApi.aprovar(id)
        : await divergenciasApi.rejeitar(id);
      setItems(prev => prev.map(d => d.id === atualizada.id ? atualizada : d));
    } catch (e: any) {
      alert(`Erro ao resolver: ${e?.response?.data?.erro ?? e.message}`);
    } finally {
      setResolvendo(r => { const c = { ...r }; delete c[id]; return c; });
    }
  }

  const pendentes  = items.filter(d => d.status === 'PENDENTE').length;
  const resolvidos = items.filter(d => d.status !== 'PENDENTE').length;

  return (
    <div className="min-h-full bg-gray-50">
      {/* Cabeçalho */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <h1 className="text-lg font-semibold text-gray-900">Fila de Divergências - Conector iSolve</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          {pendentes} pendente{pendentes !== 1 ? 's' : ''} · {resolvidos} resolvida{resolvidos !== 1 ? 's' : ''}
        </p>
      </header>

      {/* Filtros */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Tipo</label>
          <select
            value={filtroTipo}
            onChange={e => setFiltroTipo(e.target.value)}
            className="text-sm border border-gray-300 rounded px-2 py-1.5"
          >
            <option value="">Todos</option>
            <option value="INATIVACAO_COMPRADOR">Inativação</option>
            <option value="DIVERGENCIA_CAMPO">Campo divergente</option>
            <option value="REGISTRO_NOVO">Registro novo</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">Status</label>
          <select
            value={filtroStatus}
            onChange={e => setFiltroStatus(e.target.value)}
            className="text-sm border border-gray-300 rounded px-2 py-1.5"
          >
            <option value="">Todos</option>
            <option value="PENDENTE">Pendente</option>
            <option value="APROVADO">Aprovado</option>
            <option value="REJEITADO">Rejeitado</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">De</label>
          <input
            type="date"
            value={filtroDE}
            onChange={e => setFiltroDE(e.target.value)}
            className="text-sm border border-gray-300 rounded px-2 py-1.5"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">Até</label>
          <input
            type="date"
            value={filtroATE}
            onChange={e => setFiltroATE(e.target.value)}
            className="text-sm border border-gray-300 rounded px-2 py-1.5"
          />
        </div>

        <button
          onClick={carregar}
          className="text-sm bg-otm-orange text-white px-3 py-1.5 rounded hover:bg-otm-orange-dark"
        >
          Filtrar
        </button>
      </div>

      {/* Conteúdo */}
      <main className="px-6 py-4">
        {carregando && (
          <p className="text-sm text-gray-500 py-8 text-center">Carregando…</p>
        )}

        {!carregando && erro && (
          <p className="text-sm text-red-600 py-8 text-center">{erro}</p>
        )}

        {!carregando && !erro && items.length === 0 && (
          <p className="text-sm text-gray-500 py-8 text-center">Nenhuma divergência encontrada para os filtros selecionados.</p>
        )}

        {!carregando && !erro && items.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['Tipo', 'Operação', 'Entidade', 'Campo', 'Valor atual (CRM)', 'Valor proposto (iSolve)', 'Data', 'Status', 'Ações'].map(col => (
                    <th key={col} className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map(div => (
                  <tr key={div.id} className={div.status === 'PENDENTE' ? 'bg-white hover:bg-gray-50' : 'bg-gray-50 opacity-70'}>
                    <td className="px-4 py-3 whitespace-nowrap">{badgeTipo(div.tipo)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {div.operacaoId ? (
                        <a
                          href={`/?op=${div.operacaoId}`}
                          className="text-blue-600 hover:underline text-xs"
                          title={div.resolucaoAmbigua ? 'Resolução ambígua - Parte associada a mais de uma operação; mostrando a mais recente ativa.' : undefined}
                        >
                          {div.operacaoNumero ?? '-'}
                          {div.compradorPrincipalNome ? ` · ${div.compradorPrincipalNome}` : ''}
                          {div.resolucaoAmbigua && <span className="text-amber-500"> (?)</span>}
                        </a>
                      ) : (
                        <span className="text-xs text-gray-400 italic">não identificada</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="font-mono text-xs text-gray-600">{div.entidade}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="font-mono text-xs">{div.campo}</span>
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <span className="text-gray-700 break-all">{div.valorAtualCRM ?? <span className="text-gray-400 italic">null</span>}</span>
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <span className="text-gray-900 font-medium break-all">{div.valorPropostoISolve ?? <span className="text-gray-400 italic">null</span>}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-xs">{formatarData(div.criadoEm)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{badgeStatus(div.status)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {div.status === 'PENDENTE' ? (
                        <div className="flex gap-2">
                          <button
                            onClick={() => resolver(div.id, 'APROVAR')}
                            disabled={!!resolvendo[div.id]}
                            className="text-xs bg-green-600 text-white px-2.5 py-1 rounded hover:bg-green-700 disabled:opacity-50"
                          >
                            {resolvendo[div.id] === 'aprovando' ? '…' : 'Aprovar'}
                          </button>
                          <button
                            onClick={() => resolver(div.id, 'REJEITAR')}
                            disabled={!!resolvendo[div.id]}
                            className="text-xs bg-gray-200 text-gray-700 px-2.5 py-1 rounded hover:bg-gray-300 disabled:opacity-50"
                          >
                            {resolvendo[div.id] === 'rejeitando' ? '…' : 'Rejeitar'}
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">{formatarData(div.resolvidoEm)}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
